import { regexForURL } from '@modules/common/utils/isStringURL';
import { useIsDashboardEditable } from '@modules/dashboard/hooks';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, Link, Stack, Typography } from '@mui/material';
import { DashboardViewFragment } from 'generated/graphql';
import { useCallback, useState } from 'react';
import Highlighter from 'react-highlight-words';

const MAX_DESCRIPTION_LEN = 75;

interface DashboardDescriptionProps {
  dashboard: Pick<
    DashboardViewFragment,
    'description' | 'editToken' | 'status' | 'id'
  >;
  shouldShowFullDescription?: boolean;
  isReadOnly?: boolean;
}

interface HighlighterCustomTagProps {
  children: React.ReactNode;
  highlightIndex: number;
}

export const DashboardDescription = ({
  dashboard,
  isReadOnly,
  ...props
}: DashboardDescriptionProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const description = dashboard.description;

  const shouldShowButton =
    !props.shouldShowFullDescription &&
    description.length > MAX_DESCRIPTION_LEN;

  const shouldShowFullDescription =
    props.shouldShowFullDescription || !shouldShowButton || isOpen;

  const isEditable = useIsDashboardEditable();

  const shouldShowDescription = (!isReadOnly && isEditable) || !dashboard.id;

  const renderLink = useCallback(
    ({ children, highlightIndex }: HighlighterCustomTagProps) => {
      return (
        <Link
          href={description.match(regexForURL)?.[highlightIndex]}
          target="_blank"
          rel="noopener noreferrer"
        >
          {children}
        </Link>
      );
    },
    [description],
  );

  if (
    !description &&
    (props.shouldShowFullDescription || !shouldShowDescription)
  ) {
    return null;
  }

  if (!description) {
    return (
      <Box>
        <Typography component="span" variant="body2" color="text.secondary">
          Add context to your dashboard by entering a brief description here.
        </Typography>{' '}
        <Typography component="span" variant="body2" color="text.secondary">
          Click{' '}
          <EditIcon
            fontSize="inherit"
            sx={{
              color: 'text.primary',
            }}
          />{' '}
          to add.
        </Typography>
      </Box>
    );
  }

  const descriptionToDisplay = shouldShowFullDescription
    ? description
    : `${description.slice(0, MAX_DESCRIPTION_LEN)}...`;

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Typography
        variant="body2"
        color="text.secondary"
        aria-label={description}
      >
        <Highlighter
          searchWords={[regexForURL]}
          highlightTag={renderLink}
          textToHighlight={descriptionToDisplay}
        />
      </Typography>
      {shouldShowButton && (
        <Typography component="span" variant="body2" color="text.secondary">
          <Button
            color="inherit"
            component="span"
            variant="text"
            size="small"
            onClick={() => setIsOpen((isOpen) => !isOpen)}
            sx={{
              whiteSpace: 'nowrap',
              minWidth: 'max-content',
              mr: 1,
            }}
          >
            See {isOpen ? 'Less' : 'More'}
          </Button>
        </Typography>
      )}
    </Stack>
  );
};
